import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, Typography, TextField, Button, Stack, CircularProgress, Tooltip,
  Divider, Autocomplete,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { listFindings, listMembers, listSecurityHubAccounts } from '../../../services/securityhubApi';
import { listGDMembers } from '../../../services/guarddutyApi';
import { listMacieMembers } from '../../../services/macieApi';
import { listInspectorMembers } from '../../../services/inspectorApi';
import { listDetectiveMembers } from '../../../services/detectiveApi';
import { listAccessAnalyzerMembers } from '../../../services/accessAnalyzerApi';
// accessAnalyzerStatusLabel is imported, never defined here: this file already
// depends on FindingDetail, so the pair has exactly one direction and adding the
// reverse edge would make the two modules import each other.
import FindingDetail, { inspectorStatusLabel, accessAnalyzerStatusLabel } from './FindingDetail';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader, PortalSectionCard,
} from '../../../components/portal/PortalStates';
import { severityChipSx, stateChipSx } from './severity';
import { engineById } from './engines/registry';
import { standardLabel } from './standards';
import {
  CK, FONT_BODY, tableHeadRowSx, headCellSx, rowSx, bodyCellSx, listCardSx,
  statusChipSx,
} from '../portalStyles';

// The ASFF five, which is every severity CSPM, GuardDuty and Macie can emit.
const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];
// Every severity ANY engine can emit. UNTRIAGED ("AWS has not assigned a severity
// yet") comes only from Inspector, so this superset is offered only where
// Inspector rows can appear: Inspector's own list and the unified inbox. On a
// CSPM-only list it would be a chip that can never match anything.
//
// A tenant that has never enabled Inspector still sees the chip on the unified
// inbox, where it matches nothing. That is accepted: the alternative is making
// the shared inbox query per-engine enablement before it can draw its filters,
// which is a much larger coupling for a dead chip.
const ALL_SEVERITIES = [...SEVERITIES, 'UNTRIAGED'];
const WORKFLOWS = ['NEW', 'NOTIFIED', 'SUPPRESSED', 'RESOLVED'];
const COMPLIANCES = ['FAILED', 'PASSED', 'WARNING', 'NOT_AVAILABLE'];

// GuardDuty has no Security Hub workflow status — a finding is simply archived
// or not. The backend maps not-archived→NEW and archived→SUPPRESSED; show the
// user the AWS-console finding state rather than the internal workflow value.
const gdStatusLabel = (v) => (v === 'SUPPRESSED' ? 'Archived' : 'Not archived');

// Macie also has no AWS workflow status; archive is Prism-local triage. The
// engine normalizes a locally-archived finding to ARCHIVED, everything else NEW.
const macieStatusLabel = (v) => (v === 'ARCHIVED' || v === 'SUPPRESSED' ? 'Archived (Prism)' : 'Active');

// Detective has no Security Hub workflow status either: an investigation is
// ACTIVE or ARCHIVED. The engine maps ACTIVE→(NEW, ACTIVE) and
// ARCHIVED→(RESOLVED, ARCHIVED), so the value that reaches this list is the
// NORMALIZED one — testing for 'ARCHIVED' alone would label every archived
// investigation "Active", because that word lands on recordState and never on
// workflowStatus. The 'ARCHIVED' arm is belt-and-braces for a hand-built URL,
// mirroring macieStatusLabel above.
const detectiveStatusLabel = (v) => (v === 'RESOLVED' || v === 'ARCHIVED' ? 'Archived' : 'Active');

// Status filter vocabulary per engine-native list, and the label each value gets.
// The VALUES are deliberately the normalized workflowStatus the unified
// /findings/all source filters on and returns — not the engine's own AWS words.
// Inspector's engine adapter accepts NEW/SUPPRESSED/RESOLVED (mapping them to
// ACTIVE/SUPPRESSED/CLOSED) and silently DROPS an unrecognised token, so sending
// its native 'CLOSED' here would apply no filter at all and quietly return every
// row.
const STATUS_VALUES = {
  guardduty: ['NEW', 'SUPPRESSED'],
  macie: ['NEW', 'ARCHIVED'],
  inspector: ['NEW', 'SUPPRESSED', 'RESOLVED'],
  // Detective's NORMALIZED pair, never its own ACTIVE/ARCHIVED words: those land
  // on recordState, so a chip sending them would filter on a token no row carries
  // — and an unmatched status list applies no filter at all, so the chip would
  // silently return every row while looking like it worked.
  detective: ['NEW', 'RESOLVED'],
  // Access Analyzer's AWS-side vocabulary is ACTIVE | ARCHIVED | RESOLVED, but
  // Prism CACHES ONLY THE FIRST TWO — RESOLVED means the underlying access was
  // actually removed, AWS sets it and ages those rows out, so a RESOLVED chip
  // could only ever return zero rows and is deliberately not offered. The values
  // here are the NORMALIZED pair those two map to (ACTIVE→NEW, ARCHIVED→
  // SUPPRESSED); sending AWS's own words would match nothing in
  // accessAnalyzerStatusFromWorkflow, and an unmatched status list applies NO
  // filter at all, so the chip would silently return every row.
  accessanalyzer: ['NEW', 'SUPPRESSED'],
};
const STATUS_LABELS = {
  guardduty: gdStatusLabel,
  macie: macieStatusLabel,
  inspector: inspectorStatusLabel,
  detective: detectiveStatusLabel,
  accessanalyzer: accessAnalyzerStatusLabel,
};

// Filter chips share one language with the rest of the console: a selected chip
// is a pastel-triad chip (severity keeps its own severity triad), an unselected
// chip is an outlined chip of the same height so the grid never shifts.
const selectedChipSx = () => statusChipSx('info');

// Active "Type: …" / "Standard: …" chips carry a delete affordance; tint it so
// the × reads as part of the chip rather than the MUI default grey.
const removableChipSx = {
  ...statusChipSx('info'),
  '& .MuiChip-deleteIcon': {
    color: `${CK.blue}99`,
    transition: 'color 0.2s ease',
    '&:hover': { color: CK.blue },
  },
};

const unselectedChipSx = {
  height: 24,
  fontSize: '0.75rem',
  fontFamily: FONT_BODY,
  fontWeight: 500,
  color: CK.body,
  borderColor: CK.border,
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': { backgroundColor: CK.pageBg, borderColor: CK.blue },
};

export default function FindingsList({
  source = listFindings,
  subtitle,
  showEngine, // unified inbox: add an Engine column so rows are attributable
  engineKind, // 'guardduty'/'macie'/'inspector'/'detective' hide the CSPM-only compliance filter + use engine-native status values
  // Per-engine identity: a findings page carries its engine's icon and accent so
  // it reads as part of that engine, not a generic list. Defaults keep the
  // umbrella/CSPM lists on the portal blue.
  icon,
  color = CK.blue,
  // Optional { title, description, action } shown INSTEAD of the shared "no
  // findings yet" copy, and only when no filter is active (a filtered empty list
  // always means "widen the filter", whatever the engine).
  //
  // Detective is why this exists: zero investigations is its EXPECTED steady
  // state — AWS creates none on its own and Prism never calls StartInvestigation
  // — so "Findings appear here as soon as your enrolled accounts report them"
  // states the opposite of the truth and reads as a broken engine. Absent for the
  // other four engines, whose copy is unchanged.
  emptyState,
}) {
  const isGD = engineKind === 'guardduty';
  const isMacie = engineKind === 'macie';
  // Inspector's status vocabulary is AWS-SIDE (ACTIVE|SUPPRESSED|CLOSED), unlike
  // Macie's Prism-local archive flag. Do not route it through the archive toggle:
  // Prism cannot mutate an Inspector finding at all.
  const isInspector = engineKind === 'inspector';
  // Detective's rows are investigations. Its type filter is `entityType`
  // (IAM_ROLE | IAM_USER) and its status vocabulary is the normalized NEW/RESOLVED
  // pair above — everything else on this list behaves like the other engine-native
  // sources.
  const isDetective = engineKind === 'detective';
  // Access Analyzer's rows come from two analyzer families under one engine. Its
  // type filter is `findingType` (ExternalAccess | UnusedIAMRole |
  // UnusedIAMUserAccessKey | UnusedIAMUserPassword | UnusedPermission) and its
  // status vocabulary is the normalized NEW/SUPPRESSED pair above — AWS's own
  // ACTIVE/ARCHIVED words never reach this list, and RESOLVED is never cached at
  // all. Prism has no mutation for these findings, so like Inspector and Detective
  // this is a read-only list.
  const isAccessAnalyzer = engineKind === 'accessanalyzer';
  // Everything a non-CSPM engine list has in common: no compliance status, a
  // finding-type filter its source understands, and a "Status" column rather than
  // Security Hub's "Workflow".
  const isEngineNative = isGD || isMacie || isInspector || isDetective || isAccessAnalyzer;
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openEngine, setOpenEngine] = useState(null);
  // Account filter dropdown options: only accounts ENROLLED in this engine (the
  // ones that can have findings), labelled with their friendly name. Built from the
  // engine's member list joined to the realm account-name list.
  const [accountOptions, setAccountOptions] = useState([]);
  useEffect(() => {
    let active = true;
    const memberSource = isAccessAnalyzer
      ? listAccessAnalyzerMembers
      : isDetective
        ? listDetectiveMembers
        : isInspector
          ? listInspectorMembers
          : isMacie ? listMacieMembers : isGD ? listGDMembers : listMembers;
    Promise.all([memberSource(), listSecurityHubAccounts()])
      .then(([m, a]) => {
        if (!active) return;
        const nameById = {};
        (a?.data?.accounts || a?.data || []).forEach((x) => {
          if (x.accountId) nameById[x.accountId] = x.name || x.accountName || '';
        });
        const ids = [...new Set(
          (m?.data || [])
            .filter((r) => r.phase !== 'disabled' && r.phase !== 'removing')
            .map((r) => r.accountId),
        )];
        setAccountOptions(ids.map((id) => ({ accountId: id, name: nameById[id] || '' })));
      })
      .catch(() => { /* best-effort: dropdown stays empty, filtering still works */ });
    return () => { active = false; };
    // Every flag that selects memberSource belongs here: a missing dependency
    // silently reuses the previous engine's member list, which is a wrong account
    // dropdown with no error anywhere.
  }, [isGD, isMacie, isInspector, isDetective, isAccessAnalyzer]);
  // Monotonic request id: typing into a text filter (account/search) fires a
  // load() per keystroke; without this guard an earlier request (e.g. account
  // "4" -> 0 rows) can resolve AFTER the final one and clobber the results.
  const reqSeq = useRef(0);

  const severity = params.getAll('severity');
  const workflow = params.getAll('workflowStatus');
  const compliance = params.getAll('complianceStatus');
  const account = params.get('account') || '';
  const search = params.get('search') || '';
  // 'type' is a finding-type filter for the engine-native sources (GuardDuty,
  // Macie, Inspector, Detective and Access Analyzer); the CSPM source doesn't
  // accept it, so never surface it on a CSPM list. Inspector's dashboard deep-links
  // into this list with ?type=PACKAGE_VULNERABILITY, Detective's with ?type=IAM_ROLE
  // and Access Analyzer's with ?type=ExternalAccess, so the param has to survive
  // the gate. Detective's engine adapter reads it as `entityType`
  // (IAM_ROLE | IAM_USER) and Access Analyzer's as `findingType`, which is why the
  // active chip below names each one for its own engine.
  const findingType = isEngineNative ? (params.get('type') || '') : '';
  const standard = params.get('standard') || '';

  const severityKey = severity.join(',');
  const workflowKey = workflow.join(',');
  const complianceKey = compliance.join(',');

  const setParam = (key, value, isList = false) => {
    const p = new URLSearchParams(params);
    p.delete(key);
    if (isList) {
      (value || []).forEach((v) => p.append(key, v));
    } else if (value) {
      p.set(key, value);
    }
    setParams(p);
    setCursor(null);
  };

  const toggleListParam = (key, v) => {
    const cur = params.getAll(key);
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    setParam(key, next, true);
  };

  const load = useCallback(async (append = false, overrideCursor) => {
    const myReq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await source({
        severity: severityKey ? severityKey.split(',') : [],
        workflowStatus: workflowKey ? workflowKey.split(',') : [],
        complianceStatus: complianceKey ? complianceKey.split(',') : [],
        account: account ? [account] : [],
        search,
        findingType,
        standard,
        // overrideCursor avoids reading the async setCursor state on "Load more".
        cursor: append ? (overrideCursor ?? cursor) : undefined,
        limit: 50,
      });
      // Ignore a response that a newer request has already superseded.
      if (myReq !== reqSeq.current) return;
      const data = res?.data || {};
      setRows((prev) => (append ? [...prev, ...(data.findings || [])] : (data.findings || [])));
      setNextCursor(data.nextCursor || null);
      setError(null);
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      setError(e.message);
    } finally {
      if (myReq === reqSeq.current) setLoading(false);
    }
  }, [source, severityKey, workflowKey, complianceKey, account, search, findingType, standard, cursor]);

  useEffect(() => {
    setCursor(null);
    // Clear the previous filter's rows immediately so stale findings from a
    // different engine/filter don't stay on screen while the new query loads
    // (otherwise switching to GuardDuty briefly shows the full page of CSPM rows).
    setRows([]);
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severityKey, workflowKey, complianceKey, account, search, findingType, standard]);

  const activeFilterCount =
    severity.length + workflow.length + compliance.length +
    (account ? 1 : 0) + (search ? 1 : 0) + (findingType ? 1 : 0) + (standard ? 1 : 0);

  const clearAll = () => {
    setParams(new URLSearchParams());
    setCursor(null);
  };

  const colCount = showEngine ? 8 : 7;
  const shownLabel = `${rows.length}${nextCursor ? '+' : ''} shown`;

  // The severity values this list offers as chips. UNTRIAGED comes only from
  // Inspector, so the superset is offered only where Inspector rows can appear.
  const severityOptions = (isInspector || showEngine) ? ALL_SEVERITIES : SEVERITIES;
  // A severity in the URL that has no chip is otherwise INVISIBLE: the chip row
  // shows nothing selected while the list is genuinely filtered, and the only hint
  // is the "Clear filters (1)" count. Detective's dashboard deep-links exactly such
  // a value — severity=UNRATED, the sentinel its backend filter translates to
  // `severity = ''` — so the filter is surfaced the same way `type` and `standard`
  // already are: a removable chip that names it. Empty for every other engine,
  // whose severities are all chips, so nothing changes for them.
  const offChipSeverities = severity.filter((v) => !severityOptions.includes(v));

  const filterGroup = (label, values, selected, paramKey, chipSx, labelFn) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={{ display: 'block', mb: 0.75, color: CK.muted, letterSpacing: '0.08em', fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 0.75 }}>
        {values.map((v) => {
          const on = selected.includes(v);
          return (
            <Chip
              key={v}
              label={labelFn ? labelFn(v) : v}
              size="small"
              sx={on ? (chipSx || selectedChipSx)(v) : unselectedChipSx}
              onClick={() => toggleListParam(paramKey, v)}
              variant={on ? 'filled' : 'outlined'}
            />
          );
        })}
      </Stack>
    </Box>
  );

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={icon || <FactCheckIcon sx={{ fontSize: 24 }} />}
          color={color}
          title="Findings"
          subtitle={`${subtitle != null ? subtitle : 'Findings'} • ${shownLabel}`}
          action={activeFilterCount > 0 ? (
            <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={clearAll}>
              Clear filters ({activeFilterCount})
            </Button>
          ) : null}
        />
      </motion.div>

      <motion.div variants={sectionVariants}>
      <PortalSectionCard
        icon={<FilterListIcon />}
        color={color}
        title="Filters"
        subtitle="Narrow the list by account, severity or status"
        action={activeFilterCount > 0 ? (
          <Chip size="small" label={`${activeFilterCount} active`} sx={statusChipSx('info')} />
        ) : null}
        sx={{ mb: 3 }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Autocomplete
              size="small"
              options={accountOptions}
              value={accountOptions.find((a) => a.accountId === account) || (account ? { accountId: account, name: '' } : null)}
              onChange={(_, v) => setParam('account', v ? v.accountId : '')}
              getOptionLabel={(o) => (o.name ? `${o.name} (${o.accountId})` : o.accountId)}
              isOptionEqualToValue={(o, v) => o.accountId === v.accountId}
              renderInput={(p) => <TextField {...p} label="Account" placeholder="All accounts" />}
              sx={{ minWidth: 260 }}
            />
            <TextField
              size="small"
              label="Search title"
              value={search}
              onChange={(e) => setParam('search', e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>

          {(findingType || standard || offChipSeverities.length > 0) && (
            <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 0.75 }}>
              {findingType && (
                <Chip
                  label={`${isDetective ? 'Entity type' : isAccessAnalyzer ? 'Finding type' : 'Type'}: ${findingType}`}
                  onDelete={() => setParam('type', '')}
                  size="small"
                  sx={removableChipSx}
                />
              )}
              {offChipSeverities.map((v) => (
                <Chip
                  key={v}
                  label={`Severity: ${v}`}
                  onDelete={() => toggleListParam('severity', v)}
                  size="small"
                  sx={removableChipSx}
                />
              ))}
              {standard && (
                <Chip
                  label={`Standard: ${standardLabel(standard)}`}
                  onDelete={() => setParam('standard', '')}
                  size="small"
                  sx={removableChipSx}
                />
              )}
            </Stack>
          )}

          <Divider />

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
            {filterGroup(
              'Severity',
              severityOptions,
              severity,
              'severity',
              severityChipSx,
            )}
            {isEngineNative
              ? filterGroup(
                  'Status',
                  STATUS_VALUES[engineKind],
                  workflow,
                  'workflowStatus',
                  null,
                  STATUS_LABELS[engineKind],
                )
              : filterGroup('Workflow status', WORKFLOWS, workflow, 'workflowStatus')}
            {!isEngineNative && filterGroup('Compliance status', COMPLIANCES, compliance, 'complianceStatus')}
          </Stack>
        </Stack>
      </PortalSectionCard>
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load(false)} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Box sx={{ px: 3, pt: 3 }}>
          <PortalSectionHeader
            icon={icon || <FactCheckIcon />}
            color={color}
            title="Results"
            subtitle={shownLabel}
          />
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadRowSx}>
              <TableCell sx={headCellSx}>Severity</TableCell>
              {showEngine && <TableCell sx={headCellSx}>Engine</TableCell>}
              <TableCell sx={headCellSx}>Title</TableCell>
              {/* "Resource type", not "Resource": the cell below renders
                  f.resourceType, which is genuinely a TYPE for all five engines —
                  deriveGDResource emits Instance/S3Bucket/AccessKey/Lambda,
                  deriveMacieFindingResource emits S3Bucket/S3Object, CSPM and
                  Inspector copy AWS's own resource type, and Detective emits
                  IAM_ROLE/IAM_USER. Do NOT "fix" this by switching the cell to
                  f.resource: that field is the real identifier (row.ResourceID
                  for four engines, row.EntityARN for Detective) and IS populated
                  everywhere, but it holds full ARNs that would overflow this
                  narrow column and would change what four already-shipped
                  capabilities display. The identifier stays in the detail
                  drawer, where each engine's body already renders it. */}
              <TableCell sx={headCellSx}>Resource type</TableCell>
              <TableCell sx={headCellSx}>Account</TableCell>
              <TableCell sx={headCellSx}>Region</TableCell>
              <TableCell sx={headCellSx}>{isEngineNative ? 'Status' : 'Workflow'}</TableCell>
              <TableCell sx={headCellSx}>Last observed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ borderBottom: 'none' }}>
                  <PortalLoading label="Loading findings…" color={color} />
                </TableCell>
              </TableRow>
            )}
            {rows.map((f, i) => (
              <TableRow
                key={f.findingId}
                hover
                sx={{ ...rowSx(i), cursor: 'pointer' }}
                onClick={() => { setOpenId(f.findingId); setOpenEngine(f.engine); }}
              >
                <TableCell sx={bodyCellSx}>
                  {/* A row with NO severity renders as an explicit "Unrated" chip
                      in the neutral state colour, never as an empty pill and never
                      folded into INFORMATIONAL. Only Detective produces one — an
                      investigation carries no severity until its report has been
                      fetched — and it is the same word the Detective dashboard's
                      sixth tile and the detail drawer use, so the three cannot
                      disagree about what a blank severity means. The other four
                      engines always emit a band, so this is a no-op for them. */}
                  <Chip
                    label={f.severity || 'Unrated'}
                    size="small"
                    sx={f.severity ? severityChipSx(f.severity) : stateChipSx}
                  />
                </TableCell>
                {showEngine && (
                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={engineById(f.engine)?.label || f.engine || 'Unknown'}
                      size="small"
                      sx={statusChipSx('info')}
                    />
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    ...bodyCellSx,
                    maxWidth: 380,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={f.title || ''}><span>{f.title}</span></Tooltip>
                </TableCell>
                <TableCell sx={bodyCellSx}>{f.resourceType}</TableCell>
                <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{f.accountId}</TableCell>
                <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{f.region}</TableCell>
                <TableCell sx={bodyCellSx}>
                  {f.workflowStatus && (
                    <Chip
                      label={STATUS_LABELS[engineKind] ? STATUS_LABELS[engineKind](f.workflowStatus) : f.workflowStatus}
                      size="small"
                      sx={stateChipSx}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{new Date(f.lastObservedAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ borderBottom: 'none' }}>
                  {/* A FILTERED empty list always means "widen the filter",
                      whatever the engine, so the override applies only to the
                      unfiltered case — where "no rows" is a statement about the
                      engine and some engines need to explain it. */}
                  <PortalEmptyState
                    icon={icon || <FactCheckIcon sx={{ fontSize: 28 }} />}
                    color={color}
                    title={activeFilterCount > 0
                      ? 'No findings match these filters'
                      : (emptyState?.title || 'No findings yet')}
                    description={activeFilterCount > 0
                      ? 'Clear a filter or widen the search to see more findings.'
                      : (emptyState?.description
                        || 'Findings appear here as soon as your enrolled accounts report them.')}
                    action={activeFilterCount > 0 ? null : emptyState?.action}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      {nextCursor && (
        <motion.div variants={sectionVariants}>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} /> : null}
              onClick={() => { setCursor(nextCursor); load(true, nextCursor); }}
            >
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        </motion.div>
      )}

      <FindingDetail
        findingId={openId}
        engine={openEngine}
        open={!!openId}
        onClose={() => { setOpenId(null); setOpenEngine(null); }}
        onUpdated={() => { setCursor(null); load(false); }}
      />
    </motion.div>
  );
}
